/**
 * Training Files API Service (NC-636)
 *
 * Per-workload Nutzungsdaten upload/list/delete. Mirrors installersApi
 * (web-portal/src/services/workloadsService.ts:136-288) for upload-flow
 * parity with installer-shaped artifacts, but without the versioning /
 * markAsLatest concept (tutors overwrite the same file when they want a
 * new version; the optional description carries human-readable version).
 */

import apiClient from './api';
import {
  ApiResponse,
  TrainingFileMetadata,
  TrainingFileStatus,
  TrainingFileCategory,
} from './types';
import SparkMD5 from 'spark-md5';
import { UploadHttpError } from './uploadHttpError';

interface UploadUrlResponse {
  uploadUrl: string | null; // null for kind=folder (nothing to upload)
  blobName: string;
  fileId: string;
}

interface ConfirmResponse {
  workloadId: string;
  fileId: string;
  verified: boolean;
  status: TrainingFileStatus;
}

export const trainingFilesApi = {
  async list(workloadId: string): Promise<TrainingFileMetadata[]> {
    const response = await apiClient.get<ApiResponse<TrainingFileMetadata[]>>(
      '/training-files',
      { params: { workloadId } }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch training files');
    }
    return response.data.data;
  },

  async requestUploadUrl(params: {
    workloadId: string;
    fileName?: string;
    description?: string;
    category?: TrainingFileCategory;
    // NC-1095: optional folder target + run scope + folder nodes.
    relativePath?: string;
    runId?: string;
    kind?: 'file' | 'folder';
  }): Promise<UploadUrlResponse> {
    const response = await apiClient.post<ApiResponse<UploadUrlResponse>>(
      '/training-files-upload-url',
      params
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to request upload URL');
    }
    return response.data.data;
  },

  /**
   * NC-1095: create an empty folder node in the structure template
   * (or as run-scoped overlay when runId is given). No blob upload involved.
   */
  async createFolder(params: {
    workloadId: string;
    relativePath: string;
    runId?: string;
    description?: string;
  }): Promise<UploadUrlResponse> {
    return this.requestUploadUrl({ ...params, kind: 'folder' });
  },

  /**
   * NC-1001: sets `x-ms-blob-content-md5` so Azure persists the blob's
   * Content-MD5 property; otherwise the server-side confirm reads an empty
   * property and 400s. `md5Base64` MUST be base64 of the raw digest (not hex).
   */
  async uploadFile(
    uploadUrl: string,
    file: File,
    md5Base64: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress((event.loaded / event.total) * 100);
        }
      });

      // NC-1722: typed rejections so the dialog can hand the failure to the
      // error reporter (the PUT bypasses the apiClient interceptor). Message
      // texts are unchanged — the dialog shows them verbatim.
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new UploadHttpError(xhr.status, uploadUrl, `Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new UploadHttpError(0, uploadUrl, 'Upload failed due to network error'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      if (md5Base64) {
        // NC-1001: persist the blob's Content-MD5 so confirm can verify integrity.
        xhr.setRequestHeader('x-ms-blob-content-md5', md5Base64);
      }
      xhr.send(file);
    });
  },

  async confirmUpload(params: {
    fileId: string;
    workloadId: string;
    md5Hash: string;
    sizeBytes: number;
  }): Promise<ConfirmResponse> {
    const response = await apiClient.post<ApiResponse<ConfirmResponse>>(
      '/training-files-confirm',
      params
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to confirm upload');
    }
    return response.data.data;
  },

  async delete(
    fileId: string,
    workloadId: string
  ): Promise<{ fileId: string; blobName: string }> {
    const response = await apiClient.delete<
      ApiResponse<{ fileId: string; blobName: string }>
    >(`/training-files/${fileId}`, { params: { workloadId } });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to delete training file');
    }
    return response.data.data;
  },

  /**
   * NC-1001: returns base64 of the raw 16-byte digest (matches Azure
   * Content-MD5 + the server-side verifyBlobIntegrity contract). Hex 400s.
   */
  async computeMD5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const blobSlice = File.prototype.slice;
      const chunkSize = 2097152;
      const chunks = Math.ceil(file.size / chunkSize);
      let currentChunk = 0;
      const spark = new SparkMD5.ArrayBuffer();
      const fileReader = new FileReader();

      fileReader.onload = (e) => {
        spark.append(e.target?.result as ArrayBuffer);
        currentChunk++;

        if (currentChunk < chunks) {
          loadNext();
        } else {
          // end(true) → raw binary string; btoa → base64 (Azure Content-MD5 form).
          resolve(btoa(spark.end(true)));
        }
      };

      fileReader.onerror = () => {
        reject(new Error('Failed to read file for MD5 computation'));
      };

      function loadNext() {
        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
      }

      loadNext();
    });
  },
};
